import React from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { EMAIL_VIEWS } from "../../lib/emailViewConfig";

/**
 * EmailViewSelector — the highest-hierarchy control in the Email control
 * bar. Visually distinct from the ordinary Date Range / Environment
 * filters via a restrained indigo accent treatment on the trigger; its
 * open state still surfaces each view's description, not just a plain
 * option list.
 *
 * Checkpoint 3.3: the closed trigger stopped showing a leading per-view
 * icon (e.g. the Overview "layers" icon) — redundant once the external
 * "Email" label (added in Checkpoint 3.4) already names the control.
 *
 * Checkpoint 3.4: this is now paired with an external "Email:" label
 * (owned by EmailDashboardPanel, matching the existing "Date Range:" /
 * "Environment:" pattern) instead of an internal "View" eyebrow, so the
 * trigger itself is a single-line control showing just the selected view
 * name — no separate label row inside the trigger.
 *
 * Checkpoint 3.4A: 180px -> 170px, to help the six-control Performance
 * row fit one row at normal desktop widths. Longer view names (e.g.
 * "Creative / Asset Performance") truncate via the trigger's inherited
 * [&>span]:line-clamp-1 (see ui/select.jsx) rather than forcing extra
 * width — the same truncation this control already relied on at 180px.
 *
 * Checkpoint 3.4C — two preview-verified corrections:
 *
 * 1. Persistent white border: ui/select.jsx's shared SelectTrigger base
 *    classes use `focus:ring-1 focus:ring-ring`, and `--ring` resolves to
 *    `0 0% 83.1%` (near-white) in this app's dark theme (see index.css).
 *    `:focus` (unlike `:focus-visible`) stays matched after Radix returns
 *    DOM focus to the trigger on close — including a plain mouse-driven
 *    selection — so the near-white ring appeared "stuck" after picking a
 *    view, not just during real keyboard navigation. Rather than patch
 *    the shared ui/select.jsx primitive (which every Select in the app
 *    uses), this trigger neutralizes plain `:focus` locally
 *    (`focus:ring-0`) and asserts its own on-brand indigo ring only for
 *    genuine `:focus-visible` keyboard navigation — same
 *    focus-visible-only pattern already used by WarRoomPrimaryNav.
 *
 * 2. Broken closed-state layout: `<SelectValue />` with no children mirrors
 *    the ENTIRE selected SelectItem's content into the trigger by default
 *    (per Radix: "If not provided the selected item's content will be
 *    rendered") — meaning the closed trigger was rendering the full
 *    icon + flex-col{label, description} block from the dropdown option,
 *    squeezed into a ~170px box, which is what produced the clipped title
 *    and the barely-readable description bleeding into the closed state.
 *    Passing SelectValue explicit children (just the label text) replaces
 *    that auto-mirror entirely; the icon now renders once, as a sibling,
 *    not part of the value slot.
 *
 * Checkpoint 3.4C also dropped the fixed `w-[170px]` in favor of
 * `w-full min-w-0 flex-1` — this control's width is now owned by its
 * wrapping group div in EmailDashboardPanel (`flex-1 min-w-[130px]`), the
 * same fluid pattern used by every other control in the row, so it
 * shrinks/grows in step with its siblings instead of holding a fixed px
 * regardless of how much room the row actually has.
 *
 * Checkpoint 3.4D: dropped the closed-trigger icon entirely (real
 * Emergent preview still showed a cramped/troublesome closed state with
 * it present, even after 3.4C's SelectValue-children fix). The closed
 * trigger is now just the label + chevron — "Email:" (external label) +
 * "[ Performance ▼ ]" — the simplest reliable composition. The icon still
 * only ever lived in the closed trigger; dropdown OPTIONS keep their own
 * icon/label/description, unaffected.
 */
export default function EmailViewSelector({ view, onChange }) {
  const selectedView = EMAIL_VIEWS.find((v) => v.id === view) || EMAIL_VIEWS[0];

  return (
    <Select value={view} onValueChange={onChange}>
      <SelectTrigger
        className="w-full min-w-0 flex-1 justify-between gap-2 border-indigo-400/30 bg-indigo-500/10 font-semibold text-slate-100 hover:bg-indigo-500/15 focus:ring-0 focus-visible:ring-2 focus-visible:ring-indigo-400"
        data-testid="email-view-select"
      >
        {/* Radix's Select.Value doesn't reliably forward className to its
            rendered node, so the truncation/alignment styling lives on a
            plain span we fully control, passed as Value's children. */}
        <SelectValue>
          <span className="block truncate text-left" data-testid="email-view-select-value">
            {selectedView.label}
          </span>
        </SelectValue>
      </SelectTrigger>
      <SelectContent data-testid="email-view-select-content">
        {EMAIL_VIEWS.map((v) => {
          const Icon = v.icon;
          return (
            <SelectItem
              key={v.id}
              value={v.id}
              textValue={v.label}
              className="py-2"
              data-testid={`email-view-option-${v.id}`}
            >
              <div className="flex items-center gap-2">
                <Icon className="h-4 w-4 shrink-0 text-slate-300" />
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-slate-100">{v.label}</span>
                  <span className="text-xs text-slate-400">{v.description}</span>
                </div>
              </div>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}
